import { Info } from 'lucide-react';
import { FIELD_CLS, LABEL_CLS } from '../../styles/tokens';

interface Props {
  /** True when at least one plan item flags low_flow — surfaces a tighter
   *  pump-rate guidance banner. */
  lowFlow: boolean;
  purgeMethod: string;
  setPurgeMethod: (v: string) => void;
  purgeVolumeL: string;
  setPurgeVolumeL: (v: string) => void;
  pumpRateLpm: string;
  setPumpRateLpm: (v: string) => void;
}

const METHODS = [
  { value: 'peristaltic', label: 'Peristaltic pump' },
  { value: 'submersible', label: 'Submersible pump' },
  { value: 'bailer', label: 'Bailer' },
  { value: 'passive', label: 'Passive (no purge)' },
] as const;

/** Purge step — capture method, total purged volume, and pump rate (when
 *  applicable). When low_flow is true, shows the EPA guidance hint and
 *  highlights pump rates above the 500 mL/min threshold. */
export function PurgeStep({
  lowFlow,
  purgeMethod, setPurgeMethod,
  purgeVolumeL, setPurgeVolumeL,
  pumpRateLpm, setPumpRateLpm,
}: Props) {
  const rate = Number(pumpRateLpm);
  const overLowFlow = lowFlow && Number.isFinite(rate) && rate > 0.5; // L/min ≈ 500 mL/min

  return (
    <div className="space-y-4">
      <header>
        <h2 className="text-lg font-semibold text-mono-100">Purge</h2>
        <p className="text-xs text-mono-400 mt-0.5">
          {lowFlow
            ? 'Low-flow purge — keep pump rate ≤ 500 mL/min and confirm field params stabilize before sample collection.'
            : 'Record the method, total volume purged, and pump rate (when applicable).'}
        </p>
      </header>

      {lowFlow && (
        <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 p-3 text-xs text-cyan-200 flex items-start gap-2">
          <Info className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={2} />
          <div>
            <p className="font-medium">Low-flow protocol</p>
            <p className="text-mono-400 mt-0.5">
              EPA Region 6 expects ≤ 500 mL/min, ≤ 0.1 m drawdown, and 3 consecutive stabilized field-parameter readings before sample collection.
            </p>
          </div>
        </div>
      )}

      <label className="block">
        <span className={LABEL_CLS}>Method</span>
        <div className="grid grid-cols-2 gap-1.5 mt-1">
          {METHODS.map((m) => {
            const sel = m.value === purgeMethod;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => setPurgeMethod(sel ? '' : m.value)}
                className={`min-h-touch rounded-md border px-3 py-2 text-sm transition-colors ${sel ? 'bg-cyan-500/15 text-cyan-200 border-cyan-500/40 ring-1 ring-cyan-500/30' : 'bg-mono-900 text-mono-200 border-mono-700 active:bg-mono-800'}`}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </label>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL_CLS}>Volume purged <span className="text-mono-600 normal-case tracking-normal">(L)</span></span>
          <input
            type="text"
            inputMode="decimal"
            className={`${FIELD_CLS} mt-1`}
            value={purgeVolumeL}
            onChange={(e) => setPurgeVolumeL(e.target.value)}
            placeholder="e.g. 12.5"
          />
        </label>
        <label className="block">
          <span className={LABEL_CLS}>Pump rate <span className="text-mono-600 normal-case tracking-normal">(L/min)</span></span>
          <input
            type="text"
            inputMode="decimal"
            className={`${FIELD_CLS} mt-1 ${overLowFlow ? 'border-amber-500 focus:border-amber-400' : ''}`}
            value={pumpRateLpm}
            onChange={(e) => setPumpRateLpm(e.target.value)}
            placeholder="e.g. 0.3"
          />
          {overLowFlow && (
            <p className="text-[11px] text-amber-400 mt-1">
              Above 500 mL/min — not strictly low-flow. Document if intentional.
            </p>
          )}
        </label>
      </div>
    </div>
  );
}
